import { Schemas } from "@kollectivia/shared";

export function canonicalJson(value: unknown): string {
  return JSON.stringify(value, (_key, item: unknown) =>
    item && typeof item === "object" && !Array.isArray(item)
      ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b)))
      : item,
  );
}

/** Reject unsupported content instead of silently accepting a stripped schema. */
export function parseSchemaImport(text: string) {
  const raw: unknown = JSON.parse(text);
  const parsed = Schemas.FormVersionSchemaContract.parse(raw);
  if (canonicalJson(raw) !== canonicalJson(parsed)) throw new Error("unsupported_content");
  Schemas.FormVersionDraftSchemaContract.parse(raw);
  return parsed;
}

export type ImportedSchema = ReturnType<typeof parseSchemaImport>;
export type SchemaDifference = {
  path: string;
  kind: "added" | "removed" | "changed";
  before?: unknown;
  after?: unknown;
};

/** Key-based comparison includes order, complete fields and all section metadata. */
export function schemaImportDifferences(before: ImportedSchema, after: ImportedSchema) {
  const result: SchemaDifference[] = [];
  const add = (path: string, left: unknown, right: unknown) => {
    if (canonicalJson(left) === canonicalJson(right)) return;
    result.push({
      path,
      kind: left === undefined ? "added" : right === undefined ? "removed" : "changed",
      before: left,
      after: right,
    });
  };
  add("version_metadata", before.version_metadata, after.version_metadata);
  add(
    "sections.order",
    before.sections.map((s) => s.key),
    after.sections.map((s) => s.key),
  );
  const oldSections = new Map(before.sections.map((s) => [s.key, s]));
  const newSections = new Map(after.sections.map((s) => [s.key, s]));
  if (oldSections.size !== before.sections.length || newSections.size !== after.sections.length) {
    add("sections", before.sections, after.sections);
    return result;
  }
  for (const key of new Set([...oldSections.keys(), ...newSections.keys()])) {
    const left = oldSections.get(key),
      right = newSections.get(key);
    if (!left || !right) {
      add(`sections.${key}`, left, right);
      continue;
    }
    const { fields: oldFields, ...oldMeta } = left;
    const { fields: newFields, ...newMeta } = right;
    add(`sections.${key}.metadata`, oldMeta, newMeta);
    add(
      `sections.${key}.fields.order`,
      oldFields.map((f) => f.key),
      newFields.map((f) => f.key),
    );
    const oldMap = new Map(oldFields.map((f) => [f.key, f]));
    const newMap = new Map(newFields.map((f) => [f.key, f]));
    if (oldMap.size !== oldFields.length || newMap.size !== newFields.length) {
      add(`sections.${key}.fields`, oldFields, newFields);
      continue;
    }
    for (const field of new Set([...oldMap.keys(), ...newMap.keys()]))
      add(`sections.${key}.fields.${field}`, oldMap.get(field), newMap.get(field));
  }
  return result;
}
